import React, { useMemo, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Card } from '@/components/ui/card';
import { SortableMatchRow } from './SortableMatchRow';
import { FIELD_SPORTS } from '@/lib/sportStats';
import { CricketScore } from './CricketScore';
import { CricketScoreEditor } from './CricketScoreEditor';
import {
  Lock, Unlock, Edit3, Save, X, GripVertical, Loader2, BarChart3, Plus, Users, Trash2, ClipboardList, Printer
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ScheduleTableView({
  currentEvent,
  filteredSchedule,
  isPro,
  canScore,
  dragEnabled,
  setDragEnabled,
  isSavingOrder,
  sensors,
  handleDragEnd,
  editingDateTime,
  dateTimeInputs,
  setDateTimeInputs,
  handleEditDateTime,
  handleSaveDateTime,
  handleCancelDateTime,
  handleVenueChange,
  handleAddVenue,
  editingScore,
  scoreInputs,
  setScoreInputs,
  isVolleyball = false,
  isCricket = false,
  setInputs = [],
  setSetInputs = () => {},
  handleEditScore,
  handleSaveScore,
  handleCancelScore,
  handleMarkForfeit,
  handleLockToggle,
  getStageBadgeClass,
  onAssignTeams,
  onAddMatch,
  onDeleteMatch,
  onBulkDeleteMatches,
  onEnterBoxScore,
}) {
  // Soccer matches happen on fields, not courts — swap the header label so
  // the schedule table reads naturally for soccer leagues without changing
  // the underlying `match.court` numeric field.
  const isSoccer = FIELD_SPORTS.includes((currentEvent?.sport || '').toLowerCase());

  // ----- Bulk-selection state (Pro-only) -----------------------------------
  // Selection lives local to the table so filter changes / reorders don't
  // leak state up to the parent. IDs that vanish from the current filtered
  // view are pruned automatically so the "N selected" counter stays honest.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const filteredIds = useMemo(
    () => filteredSchedule.map((m) => m.id),
    [filteredSchedule],
  );
  const filteredIdSet = useMemo(() => new Set(filteredIds), [filteredIds]);
  useEffect(() => {
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (filteredIdSet.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [filteredIdSet]);

  const selectableEnabled = isPro && !dragEnabled && !!onBulkDeleteMatches;
  const selectedCount = selectedIds.size;
  const allFilteredSelected = selectableEnabled && filteredIds.length > 0
    && filteredIds.every((id) => selectedIds.has(id));
  const someFilteredSelected = selectableEnabled && selectedCount > 0 && !allFilteredSelected;

  const toggleRow = (matchId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(matchId);
      else next.delete(matchId);
      return next;
    });
  };
  const toggleAllFiltered = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) filteredIds.forEach((id) => next.add(id));
      else filteredIds.forEach((id) => next.delete(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const openBulkDelete = () => {
    if (!onBulkDeleteMatches || selectedCount === 0) return;
    const targets = filteredSchedule.filter((m) => selectedIds.has(m.id));
    onBulkDeleteMatches(targets, clearSelection);
  };
  return (
    <Card className="border-slate-200 shadow-card overflow-hidden">
      {isPro && (
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={dragEnabled}
                onCheckedChange={setDragEnabled}
                id="drag-mode"
                data-testid="drag-mode-toggle"
              />
              <Label htmlFor="drag-mode" className="text-sm font-medium text-slate-700">
                Reorder Mode
              </Label>
            </div>
            {dragEnabled && (
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <GripVertical className="w-3 h-3" />
                Drag matches to reorder
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isSavingOrder && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </div>
            )}
            {selectableEnabled && selectedCount > 0 && (
              <button
                type="button"
                onClick={openBulkDelete}
                className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white transition-colors"
                data-testid="bulk-delete-matches-btn"
                title="Delete selected matches"
              >
                <Trash2 className="w-4 h-4" /> Delete selected ({selectedCount})
              </button>
            )}
            {onAddMatch && (
              <button
                type="button"
                onClick={onAddMatch}
                className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-[#00247D] hover:bg-[#001A5C] text-white transition-colors"
                data-testid="add-match-btn"
              >
                <Plus className="w-4 h-4" /> Add Match
              </button>
            )}
            {filteredSchedule && filteredSchedule.length > 0 && (
              <a
                href={`/events/${currentEvent.id}/print-stat-sheets?ids=${filteredSchedule.map(m => m.id).join(',')}&auto=1`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md border border-[#00247D] text-[#00247D] hover:bg-[#00247D] hover:text-white transition-colors"
                data-testid="bulk-print-stat-sheets-btn"
                title="Open a print-ready sheet for every game currently visible"
              >
                <Printer className="w-4 h-4" /> Print Sheets ({filteredSchedule.length})
              </a>
            )}
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table className={`schedule-table w-full ${isCricket ? 'compact' : ''}`}>
            <thead>
              <tr>
                {isPro && dragEnabled && <th className="px-2 py-3 w-8"></th>}
                {selectableEnabled && (
                  <th className="px-2 py-3 w-8 text-center">
                    <Checkbox
                      checked={allFilteredSelected ? true : (someFilteredSelected ? 'indeterminate' : false)}
                      onCheckedChange={(v) => toggleAllFiltered(v === true)}
                      aria-label="Select all matches on this page"
                      data-testid="select-all-matches-checkbox"
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Time</th>
                {currentEvent.venues && currentEvent.venues.length > 0 && (
                  <th className="px-4 py-3 text-center">Venue</th>
                )}
                <th className="px-4 py-3 text-center">{isSoccer ? 'Field' : 'Court'}</th>
                <th className="px-4 py-3 text-center">Stage</th>
                <th className="px-4 py-3 text-right">
                  {currentEvent.rules?.home_away_enabled ? 'Home' : 'Team A'}
                </th>
                <th className="px-4 py-3 text-center">vs</th>
                <th className="px-4 py-3 text-left">
                  {currentEvent.rules?.home_away_enabled ? 'Away' : 'Team B'}
                </th>
                {canScore && <th className="px-4 py-3 text-center">Score</th>}
                <th className="px-4 py-3 text-center">Lock</th>
              </tr>
            </thead>
            <SortableContext items={filteredSchedule.map(m => m.id)} strategy={verticalListSortingStrategy}>
              <tbody>
                {filteredSchedule.length === 0 ? (
                  <tr>
                    <td colSpan={20} className="px-4 py-8 text-center text-slate-500">
                      No matches found
                    </td>
                  </tr>
                ) : (
                  filteredSchedule.map((match, i) => {
                    const homeAwayEnabled = currentEvent.rules?.home_away_enabled;
                    const isHomeTeamA = match.is_home_team_a;
                    
                    let leftTeam, rightTeam, leftLabel, rightLabel, leftTeamId, rightTeamId;
                    if (homeAwayEnabled && isHomeTeamA !== null && isHomeTeamA !== undefined) {
                      if (isHomeTeamA) {
                        leftTeam = match.team_a_name || 'TBD';
                        rightTeam = match.team_b_name || 'TBD';
                        leftTeamId = match.team_a;
                        rightTeamId = match.team_b;
                        leftLabel = 'H';
                        rightLabel = 'A';
                      } else {
                        leftTeam = match.team_b_name || 'TBD';
                        rightTeam = match.team_a_name || 'TBD';
                        leftTeamId = match.team_b;
                        rightTeamId = match.team_a;
                        leftLabel = 'H';
                        rightLabel = 'A';
                      }
                    } else {
                      leftTeam = match.team_a_name || 'TBD';
                      rightTeam = match.team_b_name || 'TBD';
                      leftTeamId = match.team_a;
                      rightTeamId = match.team_b;
                      leftLabel = null;
                      rightLabel = null;
                    }
                    const leftLogo = currentEvent.team_logos?.[leftTeamId];
                    const rightLogo = currentEvent.team_logos?.[rightTeamId];
                    const realTeamIds = new Set((currentEvent.teams || []).map(t => t.id));
                    // Pro users can always re-assign teams for any match —
                    // even after a bracket slot has already been filled —
                    // so mistakes on playoff matchups can be corrected.
                    // Round-robin matches usually shouldn't be re-assigned
                    // (their team_ids are known upfront), but manual and
                    // unresolved-bracket slots definitely need it. The
                    // icon is hover-only so this doesn't clutter the row.
                    const isRoundRobin = (match.stage || '').toUpperCase() === 'RR';
                    const slotUnresolved = !realTeamIds.has(match.team_a) || !realTeamIds.has(match.team_b);
                    const slotsNeedAssign = isPro && !dragEnabled && (
                      !isRoundRobin || slotUnresolved || match.stage === 'manual'
                    );
                    
                    return (
                      <SortableMatchRow
                        key={match.id}
                        match={match}
                        index={i}
                        isDragEnabled={isPro && dragEnabled}
                      >
                        {selectableEnabled && (
                          <td className="px-2 py-3 text-center w-8">
                            <Checkbox
                              checked={selectedIds.has(match.id)}
                              onCheckedChange={(v) => toggleRow(match.id, v === true)}
                              aria-label="Select match"
                              data-testid={`select-match-${i}`}
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          {isPro && editingDateTime === match.id ? (
                            <Input
                              type="date"
                              value={dateTimeInputs.date}
                              onChange={(e) => setDateTimeInputs(prev => ({ ...prev, date: e.target.value }))}
                              className="w-[130px] h-8 text-sm"
                              data-testid={`date-input-${i}`}
                            />
                          ) : (
                            <div className="flex items-center gap-1 group">
                              <span className="font-medium text-slate-700">
                                {match.date ? new Date(match.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                              </span>
                              {isPro && editingDateTime !== match.id && !dragEnabled && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-[#00247D]"
                                  onClick={() => handleEditDateTime(match)}
                                  data-testid={`edit-datetime-${i}`}
                                >
                                  <Edit3 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isPro && editingDateTime === match.id ? (
                            <div className="flex items-center gap-1">
                              <Input
                                type="time"
                                value={dateTimeInputs.time}
                                onChange={(e) => setDateTimeInputs(prev => ({ ...prev, time: e.target.value }))}
                                className="w-[100px] h-8 text-sm"
                                data-testid={`time-input-${i}`}
                              />
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleSaveDateTime(match.id)}
                                data-testid={`save-datetime-${i}`}
                              >
                                <Save className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-slate-400 hover:text-slate-600"
                                onClick={handleCancelDateTime}
                                data-testid={`cancel-datetime-${i}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 group">
                              <span className="text-slate-600">{match.time || '-'}</span>
                              {isPro && !dragEnabled && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-[#00247D]"
                                  onClick={() => handleEditDateTime(match)}
                                  data-testid={`edit-time-${i}`}
                                >
                                  <Edit3 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                        {currentEvent.venues && currentEvent.venues.length > 0 && (
                          <td className="px-4 py-3 text-center">
                            <Select
                              value={match.venue || ''}
                              onValueChange={async (value) => {
                                if (value === '__add_new__') {
                                  const name = handleAddVenue ? await handleAddVenue() : null;
                                  if (name) handleVenueChange(match.id, name);
                                  return;
                                }
                                handleVenueChange(match.id, value);
                              }}
                            >
                              <SelectTrigger className="w-[140px] h-8 text-xs" data-testid={`venue-select-${i}`}>
                                <SelectValue placeholder="Select venue" />
                              </SelectTrigger>
                              <SelectContent>
                                {currentEvent.venues.map((venue, idx) => (
                                  <SelectItem key={idx} value={venue}>
                                    {venue}
                                  </SelectItem>
                                ))}
                                {handleAddVenue && (
                                  <SelectItem
                                    value="__add_new__"
                                    className="text-[#00247D] font-semibold"
                                    data-testid={`venue-add-new-${i}`}
                                  >
                                    + Add new venue…
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </td>
                        )}
                        <td className="px-4 py-3 text-center">
                          <Badge variant="outline" className="font-mono">
                            {match.court}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={`stage-badge ${getStageBadgeClass(match.stage)}`}>
                            {match.match_label || match.stage || '-'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {leftLabel && (
                              <Badge className="bg-green-100 text-green-700 text-xs px-1.5">
                                {leftLabel}
                              </Badge>
                            )}
                            {leftLogo && (
                              <img src={`${API_URL}/api/files/${leftLogo}`} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" onError={(e) => { e.target.style.display='none'; }} />
                            )}
                            <span className="font-semibold text-slate-800">
                              {leftTeam}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-slate-400">vs</td>
                        <td className="px-4 py-3 text-left">
                          <div className="flex items-center gap-2 group">
                            {rightLogo && (
                              <img src={`${API_URL}/api/files/${rightLogo}`} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" onError={(e) => { e.target.style.display='none'; }} />
                            )}
                            <span className="font-semibold text-slate-800">
                              {rightTeam}
                            </span>
                            {rightLabel && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs px-1.5">
                                {rightLabel}
                              </Badge>
                            )}
                            {slotsNeedAssign && onAssignTeams && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-[#00247D]"
                                onClick={() => onAssignTeams(match)}
                                data-testid={`assign-teams-${i}`}
                                title="Manually assign team names"
                              >
                                <Users className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                        {canScore && (
                          <td className="px-4 py-3 text-center">
                            {editingScore === match.id ? (
                              <div className="flex flex-col items-center gap-1">
                                {isVolleyball ? (
                                  // Volleyball: per-set editor. Up to 5 rows
                                  // (best-of-5) so both bo3 and bo5 formats fit.
                                  <div className="flex flex-col items-center gap-1.5" data-testid={`set-editor-${i}`}>
                                    {setInputs.map((s, idx) => (
                                      <div key={idx} className="flex items-center gap-1">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider w-10 text-right">Set {idx + 1}</span>
                                        <Input
                                          type="number" min="0"
                                          value={s.a}
                                          onChange={(e) => setSetInputs(prev => prev.map((row, ri) => ri === idx ? { ...row, a: e.target.value } : row))}
                                          className="w-11 h-7 text-center text-sm p-1"
                                          data-testid={`set-${idx}-a-input-${i}`}
                                          disabled={!!match.forfeit_team}
                                        />
                                        <span className="text-slate-400">-</span>
                                        <Input
                                          type="number" min="0"
                                          value={s.b}
                                          onChange={(e) => setSetInputs(prev => prev.map((row, ri) => ri === idx ? { ...row, b: e.target.value } : row))}
                                          className="w-11 h-7 text-center text-sm p-1"
                                          data-testid={`set-${idx}-b-input-${i}`}
                                          disabled={!!match.forfeit_team}
                                        />
                                        {setInputs.length > 1 && (
                                          <Button
                                            size="icon" variant="ghost"
                                            className="h-6 w-6 text-slate-400 hover:text-rose-600"
                                            onClick={() => setSetInputs(prev => prev.filter((_, ri) => ri !== idx))}
                                            data-testid={`remove-set-${idx}-${i}`}
                                            title="Remove set"
                                          >
                                            <X className="w-3.5 h-3.5" />
                                          </Button>
                                        )}
                                      </div>
                                    ))}
                                    <div className="flex items-center gap-1 mt-0.5">
                                      {setInputs.length < 5 && (
                                        <Button
                                          size="sm" variant="outline"
                                          className="h-6 px-2 text-[10px] uppercase tracking-wider"
                                          onClick={() => setSetInputs(prev => [...prev, { a: '', b: '' }])}
                                          data-testid={`add-set-${i}`}
                                          disabled={!!match.forfeit_team}
                                        >
                                          <Plus className="w-3 h-3 mr-1" /> Set
                                        </Button>
                                      )}
                                      <Button
                                        size="icon" variant="ghost"
                                        className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                                        onClick={() => handleSaveScore(match.id)}
                                        data-testid={`save-score-${i}`}
                                      >
                                        <Save className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        size="icon" variant="ghost"
                                        className="h-7 w-7 text-slate-400 hover:text-slate-600"
                                        onClick={handleCancelScore}
                                        data-testid={`cancel-score-${i}`}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ) : isCricket ? (
                                  <div className="flex flex-col items-center gap-1.5">
                                    <CricketScoreEditor match={match} inputs={scoreInputs} setInputs={setScoreInputs} i={i}
                                      onEnter={() => handleSaveScore(match.id)} />
                                    <div className="flex items-center gap-1.5">
                                      <Button size="sm" className="h-7 px-3 text-xs bg-green-600 hover:bg-green-700 text-white"
                                        onClick={() => handleSaveScore(match.id)} data-testid={`save-score-${i}`}>
                                        <Save className="w-3.5 h-3.5 mr-1" /> Save
                                      </Button>
                                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
                                        onClick={handleCancelScore} data-testid={`cancel-score-${i}`}>
                                        <X className="w-3.5 h-3.5 mr-1" /> Cancel
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 justify-center">
                                    <Input
                                      type="number"
                                      min="0"
                                      value={scoreInputs.score_a}
                                      onChange={(e) => setScoreInputs(s => ({ ...s, score_a: e.target.value }))}
                                      className="w-12 h-8 text-center text-sm p-1"
                                      data-testid={`score-a-input-${i}`}
                                      disabled={!!match.forfeit_team}
                                    />
                                    <span className="text-slate-400">-</span>
                                    <Input
                                      type="number"
                                      min="0"
                                      value={scoreInputs.score_b}
                                      onChange={(e) => setScoreInputs(s => ({ ...s, score_b: e.target.value }))}
                                      className="w-12 h-8 text-center text-sm p-1"
                                      data-testid={`score-b-input-${i}`}
                                      disabled={!!match.forfeit_team}
                                    />
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                                      onClick={() => handleSaveScore(match.id)}
                                      data-testid={`save-score-${i}`}
                                    >
                                      <Save className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 text-slate-400 hover:text-slate-600"
                                      onClick={handleCancelScore}
                                      data-testid={`cancel-score-${i}`}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                )}
                                {handleMarkForfeit && (
                                  <div className="flex items-center gap-1 text-[10px]">
                                    <span className="text-slate-400 uppercase tracking-wider">Forfeit:</span>
                                    <Select
                                      value={match.forfeit_team || 'none'}
                                      onValueChange={(v) => handleMarkForfeit(match.id, v === 'none' ? '' : v)}
                                    >
                                      <SelectTrigger className="h-6 w-24 text-xs" data-testid={`forfeit-select-${i}`}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        <SelectItem value="a">Team A</SelectItem>
                                        <SelectItem value="b">Team B</SelectItem>
                                        <SelectItem value="both">Both</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 justify-center">
                                {match.forfeit_team ? (
                                  <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] font-bold uppercase tracking-wider" data-testid={`forfeit-badge-${i}`}>
                                    {match.forfeit_team === 'both' ? 'Double Forfeit' : `Forfeit (${match.forfeit_team === 'a' ? 'Team A' : 'Team B'})`}
                                  </Badge>
                                ) : match.score_a !== null && match.score_b !== null ? (
                                  <div className="flex flex-col items-center">
                                    {isCricket ? (
                                      <span className="font-mono font-semibold text-slate-800 flex items-start gap-2" data-testid={`cricket-score-${i}`}>
                                        <CricketScore match={match} side="a" stacked /> <span>-</span> <CricketScore match={match} side="b" stacked />
                                      </span>
                                    ) : (
                                    <span className="font-mono font-semibold text-slate-800">
                                      {match.score_a} - {match.score_b}
                                    </span>
                                    )}
                                    {isVolleyball && Array.isArray(match.sets) && match.sets.length > 0 && (
                                      // Show each set's 25-23 line under the
                                      // sets-won summary so the whole scoreline
                                      // is legible without opening the editor.
                                      <span className="text-[10px] text-slate-400 font-mono tracking-wide" data-testid={`set-breakdown-${i}`}>
                                        {match.sets
                                          .filter(s => s.a !== null && s.a !== undefined && s.b !== null && s.b !== undefined)
                                          .map(s => `${s.a}-${s.b}`)
                                          .join(', ')}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                                <div className={isCricket ? 'flex flex-col' : 'contents'}>
                                {match.stage && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-slate-400 hover:text-[#00247D]"
                                    onClick={() => handleEditScore(match)}
                                    data-testid={`edit-score-${i}`}
                                  >
                                    <Edit3 className="w-3 h-3" />
                                  </Button>
                                )}
                                {onEnterBoxScore && match.stage && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-slate-400 hover:text-amber-600"
                                    onClick={() => onEnterBoxScore(match)}
                                    title="Enter box score for this match"
                                    data-testid={`enter-box-score-${i}`}
                                  >
                                    <ClipboardList className="w-3 h-3" />
                                  </Button>
                                )}
                                </div>
                              </div>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <a
                              href={`/stats/match/${match.id}?event=${currentEvent.id}`}
                              target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center justify-center h-7 w-7 rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                              title="Game Stats"
                              data-testid={`stats-link-${i}`}
                            >
                              <BarChart3 className="w-4 h-4" />
                            </a>
                            <a
                              href={`/events/${currentEvent.id}/matches/${match.id}/print-stat-sheet?auto=1`}
                              target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center justify-center h-7 w-7 rounded-md text-slate-400 hover:text-[#00247D] hover:bg-blue-50 transition-colors"
                              title="Print blank stat sheet for this game"
                              data-testid={`print-stat-sheet-${i}`}
                            >
                              <Printer className="w-4 h-4" />
                            </a>
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`lock-match-${i}`}
                              onClick={() => handleLockToggle(match)}
                              className={`h-7 w-7 ${match.locked ? 'text-[#00247D]' : 'text-slate-400'}`}
                            >
                              {match.locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                            </Button>
                            {isPro && onDeleteMatch && (
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`delete-match-${i}`}
                                onClick={() => onDeleteMatch(match)}
                                className="h-7 w-7 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                                title="Delete this match"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </SortableMatchRow>
                    );
                  })
                )}
              </tbody>
            </SortableContext>
          </table>
        </DndContext>
      </div>
    </Card>
  );
}
